import FormGeneralError from 'components/formComponents/FormGeneralError';
import React, { useState } from 'react';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { MyDocumentsApi } from '../../api/myDocumentsApi';
import { useDispatch } from 'react-redux';
import { setUpdateMyDocumentsList } from 'reducers/misc';
import instance, { ApiResponse, useFetch } from 'api';
import { endpoints } from 'endpoints.config';
import WarningIcon from 'images/Icon_Warning_01.png';
import { Spinner } from 'components/spinner/Spinner';

type FailedDeletion = {
  id: number;
  reason: string;
};

type DeletionResponse = {
  successfulDeletions: number[];
  failedDeletions: FailedDeletion[];
};

type ErrorDeletion = {
  hasError: boolean;
  failedDeletions: FailedDeletion[];
};
type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addPluralTxt: boolean | undefined;
  ids: number[];
  selectedData: any[];
};

export type GetDeleteFoldersResponse = {
  details: {
    documentsToDelete: number[];
    foldersAndDocumentsToDelete: {
      folderAndDocument: string;
    }[];
    foldersToDelete: number[];
  };
};

export const DeleteFolders: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  addPluralTxt,
  ids,
  selectedData,
}) => {
  const dispatch = useDispatch();
  const { loading, data, error } = useFetch<GetDeleteFoldersResponse>(
    endpoints.myDocuments.deleteFolders,
    {
      paramsSerializer: (params) => {
        return ids.map((id) => `foldersIds=${id}`).join('&');
      },
      params: { foldersIds: ids },
    }
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorState, setErrorState] = useState<ErrorDeletion | undefined>();

  const deleteFolder = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    const payload = { idsToDelete: ids };
    instance
      .post<ApiResponse<any, DeletionResponse>>(
        endpoints.myDocuments.deleteFolders,
        payload
      )
      .then((res) => {
        if (res.status === 200) {
          if (
            res.data.status === '1' &&
            res.data.details.failedDeletions.length === 0
          ) {
            dispatch(setUpdateMyDocumentsList(true));
            togglePanelOpen(false);
          } else {
            // Failed deleting some users
            setErrorState({
              hasError: true,
              failedDeletions: res.data.details.failedDeletions,
            });
          }
        }
      })
      .catch((err) => {
        // Generic error
        setErrorState({ hasError: true, failedDeletions: [] });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle={`Delete file${addPluralTxt ? 's' : ''}/folder${
        addPluralTxt ? 's' : ''
      }`}
      position="right"
      primaryBtnTxt="Delete"
      secondaryBtnTxt="Cancel"
      givenClass="DeleteDocument"
      primaryBtnColor="BtnRed"
      handleClose={() => togglePanelOpen(false)}
      handlePrimaryBtn={deleteFolder}
      handleSecondaryBtn={() => togglePanelOpen(false)}
      width={560}
      isSubmitting={submitting}
      noOverflowToggle={true}
    >
      <div className="DeleteModal">
        <h4>Permanently Delete</h4>

        <p>{`Are you sure you want to delete ${
          addPluralTxt ? 'these' : 'this'
        }?`}</p>
        <div className="LineSpacer3"></div>
        <p className="BoldText">
          {`You’ll permanently lose all information about ${
            addPluralTxt ? 'these files/folders' : 'this file/folder'
          }:`}
        </p>
        {loading && !data ? (
          <Spinner positionAbsolute />
        ) : (
          <ul className="DocDeleteList">
            {data?.details.foldersAndDocumentsToDelete &&
            data?.details.foldersAndDocumentsToDelete.length > 0 ? (
              <>
                {/* {selectedData?.map((item, index) => (
                  <li className="DLItem" key={index + item.folders__id}>
                    {item.list__itemname}
                  </li>
                ))} */}
                {data?.details.foldersAndDocumentsToDelete.map((d) => (
                  <li className="DLItem" key={d.folderAndDocument}>
                    {d.folderAndDocument}
                  </li>
                ))}
              </>
            ) : null}
          </ul>
        )}

        <div className="Warning RedBack">
          <img src={WarningIcon} alt="Warning" />
          <p>This action cannot be undone</p>
        </div>
        {errorState?.hasError && (
          <div className="ErrorTxt">
            {errorState.failedDeletions.length > 0 &&
              `Error deleting the following files/folders:`}
            <ul>
              {errorState.failedDeletions.map((d, i) => (
                <li key={i}>
                  {d.id}: {d.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PopupOrPanel>
  );
};
